import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { Button, LinkButton } from '@/components/base/buttons/button';
import { Divider } from '@/components/base/divider/divider';
import { FormInput } from '@/components/base/form/form-input';
import { SocialIcon } from '@/components/foundations/social-icon';
import { useCaptureParticipantUtmFromSearch } from '@/hooks/use-capture-participant-utm-from-search';
import { RecaptchaScript, RecaptchaV2Challenge } from '@/hooks/use-recaptcha';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { utmSearchSchema } from '@/schemas/utm';
import {
  getSafeReturnTo,
  storeReturnTo,
  toAbsolutePath,
} from '@/utils/get-safe-return-to';
import { ERROR_CODES } from '@common/utils';

import { useLoginMutation } from './-auth/login-mutation';

export function loginDestinationForRoles(
  roles: readonly string[],
): '/researchers/projects' | '/participants' {
  return roles.includes('researcher')
    ? '/researchers/projects'
    : '/participants';
}

/**
 * The UTM params come from `redirectToParticipantLogin`, which bounces an
 * unauthenticated participant here with the attribution intact. They have to
 * be declared to survive validation, and captured below — the side-specific
 * page this replaces did both, and dropping either silently loses
 * abandon-before-login attribution (PX-274).
 */
const loginSearchSchema = utmSearchSchema.extend({
  returnTo: z.string().optional(),
  email: z.string().optional(),
});

export const SOCIAL_LOGIN_PROVIDERS = [
  'linkedin',
  'facebook',
  'google',
  'outlook',
] as const;

export type SocialProvider = (typeof SOCIAL_LOGIN_PROVIDERS)[number];

const socialProviderLabels: Record<SocialProvider, string> = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  google: 'Google',
  outlook: 'Outlook',
};

/**
 * Legacy SSO entry point. `returnUrl` is what its GET handler reads; see the
 * note at the call site for why that is not yet enough to resume.
 */
export function samlLoginHref(
  returnTo: string | undefined,
  websiteUrl: string,
): string {
  const params = new URLSearchParams({
    ...(returnTo && { returnUrl: toAbsolutePath(returnTo) }),
  });
  const query = params.toString();
  const path = `/auth/saml/login${query ? `?${query}` : ''}`;
  return websiteUrl ? new URL(path, websiteUrl).href : path;
}

export function socialLoginHref(
  provider: SocialProvider,
  returnTo: string | undefined,
  websiteUrl: string,
): string {
  const params = new URLSearchParams({
    activity: 'login',
    ...(returnTo && { url: toAbsolutePath(returnTo) }),
  });
  const path = `/auth/${provider}?${params.toString()}`;
  return websiteUrl ? new URL(path, websiteUrl).href : path;
}

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.user) {
      const returnTo = getSafeReturnTo(search.returnTo);
      if (returnTo) {
        throw redirect({ href: returnTo });
      }
      throw redirect({ to: loginDestinationForRoles(context.user.roles) });
    }
  },
  component: UnifiedLoginPage,
});

function UnifiedLoginPage() {
  const { env } = useRouteContext({ from: '__root__' });
  const { t } = useLingui();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const search = Route.useSearch();
  useCaptureParticipantUtmFromSearch(search);
  const returnTo = getSafeReturnTo(search.returnTo);
  const [v2Token, setV2Token] = useState<string | null>(null);
  /**
   * Latched, not derived from the latest error: once the server has demanded
   * the checkbox it demands it for every subsequent attempt, so unmounting the
   * widget the moment an attempt fails for another reason (a wrong password
   * after solving it) would make the next attempt fail the same way, forever.
   */
  const [challengeRequired, setChallengeRequired] = useState(false);
  const recaptchaResetRef = useRef<(() => void) | null>(null);
  const login = useLoginMutation();

  /**
   * Stores the destination like the email and social paths do, and hands it to
   * the legacy SSO page as `returnUrl`, which that page accepts. Resumption is
   * still not end-to-end: legacy's `POST /auth/saml/login` handler ignores
   * `returnUrl` and hard redirects to the dashboard, so carrying it through the
   * assertion (RelayState) is a change that has to happen in the website repo.
   * Storing it here means V3 already holds the destination when that lands,
   * rather than having to rediscover it.
   */
  const goToSamlLogin = () => {
    storeReturnTo(returnTo);
    globalThis.location.href = samlLoginHref(returnTo, env.WEBSITE_URL);
  };

  const form = useForm({
    defaultValues: { email: search.email ?? '', password: '' },
    validators: { onSubmit: login.schema },
    onSubmit: ({ value }) => {
      login.mutation.mutate(
        { fields: value, ...(v2Token && { captchaV2Token: v2Token }) },
        {
          onSuccess: ({ user }) => {
            analytics?.track(
              {
                action: 'completed',
                actionSubject: 'login',
                attributes: { method: 'email' },
              },
              // The navigation below reloads the document; a batched event
              // would be lost with the page.
              { immediate: true },
            );

            // Keep the intended destination through any intervening
            // verification/onboarding redirect, then resume it immediately when
            // this login is already fully authorized. The destination route owns
            // authorization and any role-specific redirect.
            storeReturnTo(returnTo);
            if (returnTo) {
              globalThis.location.href = returnTo;
              return;
            }

            void navigate({
              to: loginDestinationForRoles(user.roles),
              reloadDocument: true,
            });
          },
          onError: (error) => {
            if (error.code === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED) {
              setChallengeRequired(true);
              return;
            }
            // No password would ever have worked for this account, so there is
            // nothing for the user to correct: send them where they can
            // actually sign in instead of showing an error they cannot act on.
            if (error.code === ERROR_CODES.SSO_REQUIRED) {
              analytics?.track(
                {
                  action: 'redirected',
                  actionSubject: 'login',
                  attributes: { method: 'saml' },
                },
                { immediate: true },
              );
              goToSamlLogin();
              return;
            }
            // A solved challenge is single-use: once the attempt behind it
            // fails for any other reason, the token is spent and the widget
            // has to be re-solved.
            if (v2Token) {
              setV2Token(null);
              recaptchaResetRef.current?.();
            }
          },
        },
      );
    },
  });

  const errorCode = login.mutation.error?.code;
  const justAskedForChallenge =
    errorCode === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED;
  // Two silences, for different reasons: the challenge demand is not a failure
  // the user should read, and `SSO_REQUIRED` is already navigating away, so its
  // message would flash as an error rather than a handoff.
  const serverError =
    login.mutation.error &&
    !justAskedForChallenge &&
    errorCode !== ERROR_CODES.SSO_REQUIRED
      ? login.mutation.error.message
      : null;

  const handleSocialLogin = (provider: SocialProvider) => {
    analytics?.ui(
      {
        action: 'clicked',
        actionSubject: 'loginMethodButton',
        actionSubjectId: provider,
        source: 'loginPage',
        attributes: { method: provider },
      },
      { immediate: true },
    );

    storeReturnTo(returnTo);
    globalThis.location.href = socialLoginHref(
      provider,
      returnTo,
      env.WEBSITE_URL,
    );
  };

  return (
    <AuthLayout>
      <RecaptchaScript />
      <form
        // Native validation off in favour of the Zod schema.
        noValidate
        className="flex flex-col gap-8"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Welcome back</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>Please enter your details below.</Trans>
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <FormInput
              type="email"
              form={form}
              field="email"
              label={t`Email`}
              placeholder={t`Enter your email`}
              isRequired
              size="md"
            />
            <FormInput
              type="password"
              form={form}
              field="password"
              label={t`Password`}
              placeholder="••••••••"
              isRequired
              size="md"
            />
          </div>

          {/*
            This PR sends the side-specific reset pages' "Back to log in" here,
            so the way back out has to exist before that traffic arrives.
            Carries whatever has been typed so far over to the reset form, so
            someone who realises mid-login doesn't retype it. Subscribed rather
            than read from `form.state` so only this line re-renders per
            keystroke.
          */}
          <form.Subscribe selector={(state) => state.values.email}>
            {(email) => (
              <p className="text-sm text-tertiary">
                <Link
                  to="/forgot-password"
                  search={{ email: email.trim() || undefined }}
                  className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
                >
                  <Trans>Forgot password?</Trans>
                </Link>
              </p>
            )}
          </form.Subscribe>

          {challengeRequired && (
            <RecaptchaV2Challenge
              onVerify={setV2Token}
              onExpire={() => setV2Token(null)}
              resetRef={recaptchaResetRef}
            />
          )}

          {serverError && (
            <p className="text-sm text-error-primary">{serverError}</p>
          )}

          <Button
            type="submit"
            color="primary"
            size="lg"
            isLoading={login.mutation.isPending}
            isDisabled={
              login.mutation.isPending || (challengeRequired && !v2Token)
            }
            className="w-full"
          >
            <Trans>Log in</Trans>
          </Button>

          <Button
            type="button"
            color="link-gray"
            size="lg"
            className="w-full justify-center"
            onClick={goToSamlLogin}
          >
            <Trans>Sign in with SAML SSO</Trans>
          </Button>

          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-primary">
              <Trans>Or log in with</Trans>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {SOCIAL_LOGIN_PROVIDERS.map((provider) => (
                <Button
                  key={provider}
                  type="button"
                  color="secondary"
                  size="lg"
                  onClick={() => handleSocialLogin(provider)}
                  iconLeading={
                    <SocialIcon name={provider} className="size-5 shrink-0" />
                  }
                >
                  {socialProviderLabels[provider]}
                </Button>
              ))}
            </div>
          </div>

          <Divider />

          <p className="flex justify-end gap-2 text-sm text-tertiary">
            <Trans>New to Respondent?</Trans>{' '}
            <LinkButton color="link-color" size="md" to="/signup">
              <Trans>Sign up</Trans>
            </LinkButton>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
